import { t } from "@lingui/macro";
import { useEffect, useMemo, useState, type PropsWithChildren } from "react";
import { Platform } from "react-native";

import { useFlags, useIdentifyQueueItContext } from "@/utils/launchDarkly";
import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import {
  getQueueItClubConfig,
  QueueItClubConfiguration,
} from "./queueItConfig";
import { runNativeQueueIt } from "./runNativeQueueIt";
import { runWebQueueIt } from "./runWebQueueIt";

type QueueItState =
  | { status: "idle"; key: null }
  | { status: "pending"; key: string }
  | { status: "resolved"; key: string }
  | { status: "exited"; key: string };
type LaunchDarklyContextState =
  | { status: "idle"; key: null }
  | { status: "pending"; key: string }
  | { status: "ready"; key: string }
  | { status: "failed"; key: string };
type Props = PropsWithChildren<{
  clubSlug: string;
  configuration: QueueItClubConfiguration | null;
}>;

/**
 * A club is never queued unless it has Queue-it app configuration and
 * LaunchDarkly enables the queue for its identified club/platform context.
 * The safe fallback is always to render normally.
 */
export const QueueItGate = ({ children, clubSlug, configuration }: Props) => {
  const { queueItEnabled } = useFlags();
  const identifyQueueItContext = useIdentifyQueueItContext();
  const platform = Platform.OS === "web" ? "web" : "native";
  const launchDarklyContextKey = configuration
    ? JSON.stringify([clubSlug, platform])
    : null;
  const [launchDarklyContextState, setLaunchDarklyContextState] =
    useState<LaunchDarklyContextState>({ status: "idle", key: null });

  useEffect(() => {
    if (!launchDarklyContextKey) {
      setLaunchDarklyContextState({ status: "idle", key: null });
      return;
    }

    let isCancelled = false;

    setLaunchDarklyContextState({
      status: "pending",
      key: launchDarklyContextKey,
    });

    void identifyQueueItContext({ clubSlug, platform })
      .then(() => {
        if (!isCancelled) {
          setLaunchDarklyContextState({
            status: "ready",
            key: launchDarklyContextKey,
          });
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setLaunchDarklyContextState({
            status: "failed",
            key: launchDarklyContextKey,
          });
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [clubSlug, identifyQueueItContext, launchDarklyContextKey, platform]);

  const isLaunchDarklyContextReady =
    launchDarklyContextKey !== null &&
    launchDarklyContextState.status === "ready" &&
    launchDarklyContextState.key === launchDarklyContextKey;
  const config = useMemo(
    () =>
      isLaunchDarklyContextReady
        ? getQueueItClubConfig(queueItEnabled, configuration, platform)
        : null,
    [configuration, isLaunchDarklyContextReady, platform, queueItEnabled]
  );
  const [attempt, setAttempt] = useState(0);
  const configKey = config
    ? JSON.stringify([
        clubSlug,
        platform,
        config.customerId,
        config.eventAlias,
        attempt,
      ])
    : null;
  const customerId = config?.customerId;
  const eventAlias = config?.eventAlias;
  const [state, setState] = useState<QueueItState>({
    status: "idle",
    key: null,
  });

  useEffect(() => {
    if (!configKey || !customerId || (platform === "native" && !eventAlias)) {
      setState({ status: "idle", key: null });
      return;
    }

    let isCancelled = false;

    setState({ status: "pending", key: configKey });
    const queueItResult =
      platform === "web"
        ? runWebQueueIt(customerId, clubSlug)
        : eventAlias
        ? runNativeQueueIt(customerId, eventAlias)
        : Promise.resolve("unavailable");

    void queueItResult
      .then((result) => {
        if (isCancelled) {
          return;
        }

        setState({
          key: configKey,
          status: result === "exited" ? "exited" : "resolved",
        });
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setState({ key: configKey, status: "resolved" });
      });

    return () => {
      isCancelled = true;
    };
  }, [clubSlug, configKey, customerId, eventAlias, platform]);

  if (
    launchDarklyContextKey &&
    (launchDarklyContextState.key !== launchDarklyContextKey ||
      launchDarklyContextState.status === "pending")
  ) {
    return <Loader />;
  }

  if (!config) {
    return children;
  }

  if (state.key !== configKey || state.status === "pending") {
    return <Loader />;
  }

  if (state.status === "exited") {
    return (
      <Box
        alignItems="center"
        justifyContent="center"
        height="100%"
        width="100%"
      >
        <VStack alignItems="center" gap={4} px={6}>
          <Text
            accessibilityRole="header"
            variant="mainTitle3"
            textAlign="center"
          >
            {t`You left the waiting room.`}
          </Text>
          <Button
            accessibilityLabel={t`Rejoin waiting room`}
            onPress={() => setAttempt((currentAttempt) => currentAttempt + 1)}
          >
            {t`Rejoin waiting room`}
          </Button>
        </VStack>
      </Box>
    );
  }

  return children;
};
